import { GridConfig } from "./GridConfig";
import { PathfindingHandler } from "./pathfinding/PathfindingHandler";
import { ConnectingLineHandler } from "./ConnectingLineHandler";
import { getPossibleEndpointPositions, findDropEndpoint } from "../components/cell/grid-cell.utils";
import { DIAGRAM_EMIT_EVENTS } from "../../../diagram/modules/DiagramEvents";
import { DiagramEventEmitter } from "../../../diagram/modules/DiagramEventEmitter";
import { CellRetrievalUtils } from "../utils/CellRetrievalUtils";
import { getInstanceIDFromCanvasInstance } from "../../../diagram/diagram-assets/diagram-positioning-state";

/**
 * @description - Handles the connecting line drag functionality.
 */
export class ConnectionLineDrag {
    defaultValue = { initialPoint: null, cell: null, cellPos: null };

    sourceDrag = this.defaultValue;

    connectingLine;

    gridConfig;

    grid;

    lastUpdatedCells = [];

    lastHoveredCell;

    connectingLineHandler;

    eventEmitter;

    cellRetrieval = new CellRetrievalUtils();

    instanceUuid;

    constructor(viewInstance) {
        this.viewInstance = viewInstance;
    }

    /**
     * @description This method initializez the drag status
     * @param {Object} initialPoint - {x,y} start drag coordonates
     * @param {GridCell} cell - The cell from where the dragging is happening
     * @param {Object} cellPos  - The cell position from where it starts
     * @param {GridCell[][]} generatedGrid  - generated grid matrix
     * @param {Object} clickedElem  - The endpoint from where it starts
     */
    startDrag(initialPoint, cell, cellPos, generatedGrid, clickedElem) {
        this.instanceUuid = getInstanceIDFromCanvasInstance(cell.ctx);
        this.gridConfig = new GridConfig(this.instanceUuid);
        this.connectingLineHandler = new ConnectingLineHandler(this.instanceUuid);
        this.eventEmitter = new DiagramEventEmitter(this.instanceUuid);
        this.grid = generatedGrid;
        this.sourceDrag = {
            initialPoint: { x: initialPoint.x, y: initialPoint.y }, cell, cellPos, startEndpoint: clickedElem
        };
        cell.update({}, { draggingConnection: true });
    }

    /**
     * @description resets instance when the dataset is being changed
     */
    resetingDataSet() {
        this.defaultValue = { initialPoint: null, cell: null, cellPos: null };
        this.sourceDrag = this.defaultValue;
        this.connectingLine = null;
        this.lastHoveredCell = null;
        this.lastUpdatedCells = [];
        this.cellRetrieval = new CellRetrievalUtils();
    }

    /**
     * @description returns the grid size
     */
    getGridSize(grid) {
        return {
            x: this.gridConfig.viewPadding,
            y: this.gridConfig.viewPadding,
            w: grid[0].length * this.gridConfig.cellHeight,
            h: grid.length * this.gridConfig.cellHeight
        };
    }

    /**
     * @description Updates the connecting line currently being dragged
     * @param {Object} newCoords  - current mouse coordonates
     * @param {Object} currentCellPos  - The current cell that the mouse is currently hovering over
     */
    updateLine(newCoords, currentCellPos) {
        const { cell } = this.sourceDrag;
        if (!this.connectingLine) {
            this.connectingLine = this.connectingLineHandler.getDraggableEndpointInstance(cell.uuid);
        }
        // Access draggable end point from cell and modify its coordonates
        const currentCell = this.cellRetrieval.getCell(currentCellPos, this.grid);
        if (!currentCell) return; // prevent drawing if the cell is outside the grid

        // check to see if the line colides with the grid outer lines
        const arrowHead = { x: this.connectingLine.layout.toX, y: this.connectingLine.layout.toY, r: this.connectingLine.layout.radius };
        const gridRect = this.getGridSize(this.grid);
        const circle = { center: { x: newCoords.x, y: newCoords.y }, radius: arrowHead.r + 1 };
        const lineOne = { p1: { x: gridRect.x, y: gridRect.y }, p2: { x: gridRect.x, y: gridRect.y + gridRect.h } }; // left grid line
        const lineTwo = { p1: { x: gridRect.x, y: gridRect.y }, p2: { x: gridRect.x + gridRect.w, y: gridRect.y } }; // top grid line
        const lineThree = { p1: { x: gridRect.x, y: gridRect.y + gridRect.h }, p2: { x: gridRect.x + gridRect.w, y: gridRect.y + gridRect.h } }; // bottom grid line
        const lineFour = { p1: { x: gridRect.x + gridRect.w, y: gridRect.y }, p2: { x: gridRect.x + gridRect.w, y: gridRect.y + gridRect.h } }; // right grid line
        if (this.doesLineInterceptCircle(lineOne.p1, lineOne.p2, circle.center, circle.radius)
            || this.doesLineInterceptCircle(lineTwo.p1, lineTwo.p2, circle.center, circle.radius)
            || this.doesLineInterceptCircle(lineThree.p1, lineThree.p2, circle.center, circle.radius)
            || this.doesLineInterceptCircle(lineFour.p1, lineFour.p2, circle.center, circle.radius)) {
            return;
        }
        this.updatePiercedCells(currentCellPos, newCoords);
        if (this.lastHoveredCell) {
            this.lastHoveredCell.update({}, { connectingLineHover: false });
            this.lastHoveredCell.draw();
        }
        this.lastHoveredCell = currentCell;
        if (currentCell && currentCell.hasContent() && currentCell !== this.sourceDrag.cell) {
            if (this.connectingLineHandler.allowsIncomingConnections(currentCell.uuid)) {
                currentCell.update({}, { connectingLineHover: true });
                currentCell.draw();
            }
            this.lastHoveredCell = currentCell;
        }
        this.connectingLine.update({ toX: newCoords.x, toY: newCoords.y });
        this.connectingLine.draw();
    }

    /**
     * @description Checks if the current mouse position is intersecting the sides of the grid in order to prevent going outside of it
     */
    doesLineInterceptCircle(A, B, C, radius) {
        let dist;
        const v1x = B.x - A.x;
        const v1y = B.y - A.y;
        const v2x = C.x - A.x;
        const v2y = C.y - A.y;
        // get the unit distance along the line of the closest point to
        // circle center
        const u = (v2x * v1x + v2y * v1y) / (v1y * v1y + v1x * v1x);

        // if the point is on the line segment get the distance squared
        // from that point to the circle center
        if (u >= 0 && u <= 1) {
            dist = (A.x + v1x * u - C.x) ** 2 + (A.y + v1y * u - C.y) ** 2;
        } else {
            // if closest point not on the line segment
            // use the unit distance to determine which end is closest
            // and get dist square to circle
            dist = u < 0
                ? (A.x - C.x) ** 2 + (A.y - C.y) ** 2
                : (B.x - C.x) ** 2 + (B.y - C.y) ** 2;
        }
        return dist < radius * radius;
    }

    /**
     * @descriptino formats the initial start point coordonates into a better format
     * @param {*} points
     */
    formatPathFindingInitialPoints(points) {
        const val = {
            start: {},
            end: {}
        };
        const startPoint = {};
        startPoint[points.start.endpointLayout.dir] = {
            x: points.start.endpointLayout.x,
            y: points.start.endpointLayout.y
        };
        const endPoint = {};
        endPoint[points.end.side] = points.end.endpoint;
        val.start = startPoint;
        val.end = endPoint;
        return val;
    }

    /**
     * @description Validation to see if its the same start and end cell in order to not create a connection
     */
    isTheSameFromAndTo(from, to) {
        const fromCellData = this.connectingLineHandler.cells.get(from.uuid);
        const toCellData = this.connectingLineHandler.cells.get(to.uuid);
        for (let i = 0; i < fromCellData.outgoing.length; i++) {
            const connector = this.connectingLineHandler.connectors.get(fromCellData.outgoing[i]);
            if (connector.from === from.uuid && connector.to === to.uuid) {
                return connector.instance.uuid;
            }
        }
        return null;
    }

    /**
     * @description End drag event handler
     * @param {Object} mousePos  - current mouse position
     * @param { Object } currentCellPos - {row,col} object. Cell position in the matrix
     * @param { LabelHandler } labelHandler - labelHandler  instance
     */
    endDrag(mousePos, currentCellPos, labelHandler) {
        const dropCell = this.cellRetrieval.getCell(currentCellPos, this.grid);
        const canConnectTo = dropCell && dropCell.getContentInstance() && this.connectingLineHandler.canConnectTo(dropCell.uuid);
        if (!dropCell) {
            const sourceData = this.sourceDrag.cell.getContentInstance().data;
            const payload = { FROM_PLUGIN_UUID: sourceData.uuid, FROM_PLUGIN_ID: sourceData.id };
            this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.INVALID_CONNECTION_DRAGGED, { ...payload, reason: "OUTSIDE_GRID" });
            return;
        }
        const hasExistingPath = this.isTheSameFromAndTo(this.sourceDrag.cell, dropCell);
        // if (hasExistingPath) {
        // this.connectingLineHandler.deleteConnector(hasExistingPath);
        // }
        if (this.sourceDrag.cell === dropCell) {
            if (this.connectingLine) this.connectingLine.update({ toX: null, toY: null });
            this.updatePiercedCells(currentCellPos, mousePos);
            this.sourceDrag.cell.update({}, { draggingConnection: false });
            if (this.connectingLine) this.connectingLine.draw();
            this.sourceDrag = this.defaultValue;
            this.connectingLine = null;
            return;
        }
        let createConnection = false;
        const startCell = this.sourceDrag.cell;
        let path;
        const hasContent = dropCell && dropCell.hasContent();

        if (dropCell && canConnectTo && hasContent) {
            const dropEndpoint = findDropEndpoint(mousePos, getPossibleEndpointPositions(dropCell.getContainerInstance()), null, this.instanceUuid);

            let linePoints = { start: this.sourceDrag.startEndpoint, end: dropEndpoint };
            linePoints = this.formatPathFindingInitialPoints(linePoints);
            const pathFinder = new PathfindingHandler(this.grid, startCell, dropCell, linePoints);
            path = pathFinder.search();

            dropCell.update({}, { connectingLineHover: false });
            this.connectingLine.update({ toX: null, toY: null });
            this.updatePiercedCells(currentCellPos, mousePos);
            createConnection = true;
            // TODO ADD VALID CELL LINE DRAG DROP FUNCTIONALITY.
            // LINK THE CELLS
        }
        if (!dropCell || !canConnectTo || !hasContent) {
            if (!hasContent) {
                // todo Emit event invalid connector end drag. Empty Cell
                const content = this.sourceDrag.cell.getContentInstance();
                const fromPluginUUID = content.data.uuid;
                const fromPluginID = content.data.id;
                const payload = { FROM_PLUGIN_UUID: fromPluginUUID, FROM_PLUGIN_ID: fromPluginID };
                this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.INVALID_CONNECTION_DRAGGED, { ...payload, reason: "EMPTY_CELL" });
            }
            if (!canConnectTo && dropCell && dropCell.getContentInstance()) {
                const toCellContent = dropCell.getContentInstance();
                const toPluginUUID = toCellContent.uuid; const
                    toPluginID = toCellContent.data.id;
                const toPayload = { TO_PLUGIN_UUID: toPluginUUID, TO_PLUGIN_ID: toPluginID };
                if (toCellContent) {
                    const maxEntries = toCellContent.points.end;
                    const currentEntries = this.connectingLineHandler.cells.get(dropCell.uuid).incoming.length;
                    const content = this.sourceDrag.cell.getContentInstance();
                    const fromPluginUUID = content.data.uuid;
                    const fromPluginID = content.data.id;
                    const payload = { FROM_PLUGIN_UUID: fromPluginUUID, FROM_PLUGIN_ID: fromPluginID };
                    this.eventEmitter.emitEvent(DIAGRAM_EMIT_EVENTS.INVALID_CONNECTION_DRAGGED, { ...payload, ...toPayload, ...{ reason: "MAXIMUM_CONNECTIONS_REACHED", max: maxEntries, current: currentEntries } });
                }
            }

            this.connectingLine.update({ toX: null, toY: null });
            this.updatePiercedCells(currentCellPos, mousePos);
            this.connectingLine.draw();
        }
        this.sourceDrag.cell.update({}, { draggingConnection: false });
        if (createConnection && !hasExistingPath) {
            this.connectingLineHandler.createConnection(path, true, startCell.uuid, dropCell.uuid, this.grid, {}, labelHandler, false, this.viewInstance);
        } else if (createConnection && hasExistingPath) {
            const oldPassthroughCells = this.connectingLineHandler.connectors.get(hasExistingPath).passesThrough;
            this.connectingLineHandler.updateConnectorPathV2(hasExistingPath, path, this.grid, false, false);
            // update previously passed through cells
            oldPassthroughCells.forEach(cellUuid => {
                const cell = this.connectingLineHandler.cells.get(cellUuid);
                if (cell) cell.ref.draw();
            });
        }
        this.sourceDrag = this.defaultValue;
        this.connectingLine = null;
    }

    /**
     * @description updates pierced cells by the conencting line drag
     */
    updatePiercedCells(currentCellPos, currentMousePos) {
        const originPos = this.sourceDrag.cellPos;
        const cells = [];
        const cellsToUpdate = this.connectingLineHandler.utils.getCellsInNeedOfUpdate(this.sourceDrag.initialPoint, currentMousePos, false, this.grid, this.connectingLine);
        if (originPos.row === currentCellPos.row && originPos.col === currentCellPos.col && cellsToUpdate.indexOf(this.sourceDrag.cell) !== -1) {
            cells.push(this.sourceDrag.cell);
        }
        cells.push(...cellsToUpdate, ...this.lastUpdatedCells);
        cells.filter((cell, index, self) => self.indexOf(cell) === index);
        for (let i = 0; i < cells.length; i++) {
            cells[i].draw();
        }
        this.lastUpdatedCells = [];
        this.lastUpdatedCells.push(...cellsToUpdate);
    }

    /**
     * @description checks to see if there is a connecting line currently being dragged
     */
    activeLineDrag() {
        return !!this.sourceDrag.initialPoint;
    }
}
